import React, { FC } from "react";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import { FaPencilAlt } from "@react-icons/all-files/fa/FaPencilAlt";
import styled from "@emotion/styled";
import { EaselButton } from "../global/EaselButton";
import { mq } from "../../helpers/mediaQueries";
import { designTokens, easelColors } from "../../helpers/constants";

export interface NameFormProps {
  id: string;
  name: string;
  nameState: string;
  updateName: (params: { id: string; name: string }) => void;
  editingState: boolean;
  toggleEditingState: () => void;
  onNameChanged: (name: string) => void;
  shouldEnableEditing: boolean;
  defaultValue: string;
  maxLength?: number;
}

const sanitizeName = (name: string) => name.trim();

export const NameForm: FC<NameFormProps> = ({
  updateName,
  id,
  name,
  nameState,
  editingState,
  toggleEditingState,
  onNameChanged,
  shouldEnableEditing,
  defaultValue,
  maxLength = 100,
}: NameFormProps) => {
  const handleClick = () => {
    toggleEditingState();
  };

  const handleSubmit = () => {
    let sanitizedName = sanitizeName(nameState);

    if (sanitizedName.length === 0) {
      sanitizedName = defaultValue;
    }

    updateName({ id, name: sanitizedName });
    toggleEditingState();
  };

  const handleReset = () => {
    onNameChanged(name);
    toggleEditingState();
  };

  const showNameFormEditing = (
    <Container>
      <Group>
        <FormControl
          autoFocus
          data-testid="title-input"
          type="input"
          value={nameState}
          maxLength={maxLength}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onNameChanged(e.target.value as string)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
              handleSubmit();
            } else if (e.key === "Escape") {
              handleReset();
            }
          }}
        />
      </Group>
      <ButtonsWrapper mr>
        <EaselButton onClick={handleSubmit} size="xs" darkBackground>
          Done
        </EaselButton>
      </ButtonsWrapper>
      <ButtonsWrapper>
        <EaselButton
          onClick={handleReset}
          size="xs"
          darkBackground
          buttonType="text"
          variant="secondary"
        >
          Cancel
        </EaselButton>
      </ButtonsWrapper>
    </Container>
  );

  const showNameForm = (
    <Container>
      <CustomEaselButton
        variant="link"
        onClick={() => handleClick()}
        disabled={!shouldEnableEditing}
      >
        <Title>{nameState}</Title>
        {shouldEnableEditing && <EditIcon />}
      </CustomEaselButton>
    </Container>
  );

  return editingState && shouldEnableEditing
    ? showNameFormEditing
    : showNameForm;
};

const Container = styled(Row)({
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  margin: 0,
  width: "100%",
});

const ButtonsWrapper = styled.div<{ mr?: boolean }>((p) => ({
  display: "flex",
  marginRight: p.mr ? designTokens["ds-space-x"] : 0,
}));

const Group = styled(Form.Group)(
  mq({
    width: ["100%", "100%", "100%", 380],
    maxWidth: [380, 380, 380, null],
    marginRight: designTokens["ds-space-2x"],
    display: "flex",
    alignItems: "center",
    marginBottom: 0,
  })
);

const CustomEaselButton = styled(EaselButton)({
  display: "flex",
  padding: 0,
});

const FormControl = styled(Form.Control)({
  width: "100%",
  height: "30px",

  "&:focus": {
    borderColor: designTokens["ds-color-gray-light"],
    boxShadow: "none",
  },
});

const EditIcon = styled(FaPencilAlt)({
  color: easelColors.easelPeriwinkle300,
  marginRight: designTokens["ds-spacing-sm"],
  width: "11px",
  minWidth: "11px",
});

const Title = styled.div(
  mq({
    overflow: "hidden",
    display: "inline-block",
    color: easelColors.white,
    cursor: "pointer",
    fontSize: ["0.75rem", "1rem", null],
    fontWeight: [400, 500, null],
    marginRight: ["0.25rem", "0.5rem", null],
    maxWidth: "20vw",
    overflowX: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  })
);
